import { UPSTOX_INSTRUMENT_KEYS } from '../config/constants'
import { formatDateToYYYYMMDD } from '../lib/date-utils'

export interface UpstoxCandle {
    timestamp: string
    open: number
    high: number
    low: number
    close: number
    volume: number
    oi: number
}

export interface CandleResponse {
    status: string
    data: {
        candles: (string | number)[][]
    }
}

// ============================================================================
// In-memory cache for candle data
// Historical (day/week/month) rarely changes — cache for 30 min
// Intraday (1minute etc.) changes often — cache for 60 seconds
// ============================================================================
const CANDLE_CACHE = new Map<string, { data: UpstoxCandle[]; fetchedAt: number }>()

const CACHE_TTL: Record<string, number> = {
    day: 30 * 60 * 1000, // 30 minutes
    week: 30 * 60 * 1000, // 30 minutes
    month: 30 * 60 * 1000, // 30 minutes
    '1minute': 60 * 1000, // 60 seconds
    '3minute': 60 * 1000,
    '5minute': 60 * 1000,
    '15minute': 60 * 1000,
    '30minute': 60 * 1000,
    '60minute': 2 * 60 * 1000, // 2 minutes
}

function getCacheTTL(interval: string): number {
    return CACHE_TTL[interval] || 60 * 1000
}

/**
 * Fetch candles via the /api/candles-proxy endpoint (no tRPC).
 * Uses in-memory cache to prevent redundant network requests.
 */
async function fetchCandlesFromProxy(
    instrumentKey: string,
    interval: string,
    type: 'historical' | 'intraday',
    from?: string,
    to?: string,
): Promise<CandleResponse> {
    const params = new URLSearchParams({
        instrumentKey,
        interval,
        type,
    })
    if (from) params.set('from', from)
    if (to) params.set('to', to)

    const resp = await fetch(`/api/candles-proxy?${params}`)
    if (!resp.ok) throw new Error(`Candle proxy error: ${resp.status}`)
    return resp.json() as Promise<CandleResponse>
}

/**
 * Fetch historical candles via the server-side proxy to avoid CORS.
 * Uses in-memory cache to prevent redundant network requests.
 * Default: 30 days (1 month) of history.
 */
export const fetchHistoricalCandles = async (
    symbol: string,
    days: number = 30,
    interval: string = '1minute',
    explicitInstrumentKey?: string,
    targetDateStr?: string | null,
): Promise<UpstoxCandle[]> => {
    // ── Early exit for FO instrument keys temporarily removed to allow FO historical data ──

    // Check cache first
    const cacheKey = `${symbol}:${interval}:${days}:${explicitInstrumentKey || 'index'}:${targetDateStr || 'live'}`
    const cached = CANDLE_CACHE.get(cacheKey)
    if (cached && Date.now() - cached.fetchedAt < getCacheTTL(interval)) {
        return cached.data
    }

    try {
        const instrumentKey = explicitInstrumentKey || UPSTOX_INSTRUMENT_KEYS[symbol] || `NSE_INDEX|${symbol}`

        // Cap days to prevent 400 Bad Request from upstream Upstox API
        let effectiveDays = days
        if (interval.includes('minute')) {
            effectiveDays = Math.max(effectiveDays, 5)
            if (effectiveDays > 10) effectiveDays = 10
        } else if (effectiveDays > 365) {
            effectiveDays = 365
        }

        // 1. Fetch Historical Range (Past Data) via server proxy
        const toDate = targetDateStr ? new Date(targetDateStr) : new Date()
        if (targetDateStr) {
            toDate.setHours(23, 59, 59, 999) // Include the entire target day
        }
        
        const fromDate = new Date(toDate.getTime())
        fromDate.setDate(toDate.getDate() - effectiveDays)

        const toDateStr = formatDateToYYYYMMDD(toDate)
        const fromDateStr = formatDateToYYYYMMDD(fromDate)

        // Enable intraday (today's live data) for minute-level intervals
        const isIntradayInterval = interval.includes('minute') || interval === '60minute'

        const promises: Promise<CandleResponse>[] = [
            fetchCandlesFromProxy(instrumentKey, interval, 'historical', fromDateStr, toDateStr),
        ]

        if (isIntradayInterval && !targetDateStr) {
            promises.push(fetchCandlesFromProxy(instrumentKey, interval, 'intraday'))
        }

        // Run in parallel
        const results = await Promise.allSettled(promises)
        const historicalRes = results[0]
        const intradayRes =
            isIntradayInterval && results[1]
                ? results[1]
                : ({ status: 'rejected', reason: 'Skipped Intraday' } as PromiseSettledResult<CandleResponse>)

        let allCandles: (string | number)[][] = []

        // Process Historical
        if (historicalRes && historicalRes.status === 'fulfilled') {
            const val = (historicalRes as any).value
            if (val?.status === 'success' && val?.data?.candles) {
                allCandles = [...allCandles, ...val.data.candles]
            } else {
                console.warn('[CandleService] Historical fetch success but invalid data:', val)
            }
        } else if (historicalRes && historicalRes.status === 'rejected') {
            const err = historicalRes.reason
            let errorBody = 'No Body'
            let status = 'Unknown Status'
            try {
                if (err?.message?.includes('400')) status = '400'
                if (err?.response) {
                    status = err.response.status
                    errorBody = await err.response.text()
                }
            } catch (e) { console.warn('Ignored error in candle-service.ts:', e); }
            
            // Downgrade 400 Bad Request to warning silently, since they are largely expected for FO history
            if (status === '400') {
                console.warn(`[CandleService] Historical fetch failed (400 Bad Request) for ${instrumentKey}`)
            } else {
                console.error(`[CandleService] Historical fetch failed: ${err?.name} | ${err?.message}`, {
                    status,
                    errorBody,
                })
            }
        }

        // Process Intraday
        if (intradayRes.status === 'fulfilled') {
            const val = intradayRes.value
            if (val?.status === 'success' && val?.data?.candles) {
                allCandles = [...allCandles, ...val.data.candles]
            } else {
                console.warn('[CandleService] Intraday fetch success but invalid data:', val)
            }
        } else if (intradayRes.status === 'rejected' && isIntradayInterval) {
            const err = intradayRes.reason
            
            if (err !== 'Skipped Intraday') {
                let errorBody = 'No Body'
                let status = 'Unknown Status'
                try {
                    if (err?.message?.includes('400')) status = '400'
                    if (err?.response) {
                        status = err.response.status
                        errorBody = await err.response.text()
                    }
                } catch (e) { console.warn('Ignored error in candle-service.ts:', e); }

                if (status === '400') {
                    console.warn(`[CandleService] Intraday fetch failed (400 Bad Request) for ${instrumentKey}`)
                } else {
                    console.error(`[CandleService] Intraday fetch failed: ${err?.name || 'Unknown Error'} | ${err?.message || err}`, {
                        status,
                        errorBody,
                    })
                }
            }
        }

        if (allCandles.length === 0) {
            if (!instrumentKey.includes('_FO|')) {
                console.warn(`[CandleService] No candle data returned for ${symbol} (${interval}, ${days} days)`)
            }
            // Store empty result in cache to prevent repeated fetch spam within TTL
            CANDLE_CACHE.set(cacheKey, { data: [], fetchedAt: Date.now() })
            return []
        }

        // Deduplicate and Sort
        const candleMap = new Map<string, (string | number)[]>()

        allCandles.forEach((c) => {
            const ts = c[0] as string
            candleMap.set(ts, c)
        })

        const sortedCandles = Array.from(candleMap.values())
            .map((c) => ({
                timestamp: c[0] as string,
                open: c[1] as number,
                high: c[2] as number,
                low: c[3] as number,
                close: c[4] as number,
                volume: c[5] as number,
                oi: c[6] as number,
            }))
            .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())

        // Store in cache before returning
        CANDLE_CACHE.set(cacheKey, { data: sortedCandles, fetchedAt: Date.now() })

        return sortedCandles
    } catch (error: any) {
        console.error('[CandleService] Failed to fetch historical candles:', error)
        return []
    }
}
